import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    Modal,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    Paper,
    Stack,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Popover
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Visibility as ViewIcon,
    Hub as ZoneIcon,
    LocationCity as LocationCityIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { regionService } from '../../../services/regionService';
import RegionForm from './RegionForm';

const RegionList = ({ onDataChange }) => {
    const { handleApiCall, showSuccess, showError } = useApiHandler();
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRegion, setEditingRegion] = useState(null);
    const [viewMode, setViewMode] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [regionToDelete, setRegionToDelete] = useState(null);
    
    // Add state for popovers
    const [citiesAnchorEl, setCitiesAnchorEl] = useState(null);
    const [zonesAnchorEl, setZonesAnchorEl] = useState(null);
    const [selectedMoreInfo, setSelectedMoreInfo] = useState(null);

    useEffect(() => {
        fetchRegions();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (searchText !== '') {
            setPage(0);
            setPagination(prev => ({ ...prev, current_page: 1 }));
        }
        
        const timeoutId = setTimeout(() => {
            fetchRegions();
        }, searchText ? 300 : 0); // Debounce only for search

        return () => clearTimeout(timeoutId);
    }, [searchText]);

    const fetchRegions = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1, // Convert 0-based to 1-based
                per_page: rowsPerPage
            };
            
            if (searchText) params.search = searchText;

            const response = await regionService.getAllRegions(params);
            
            if (response.success) {
                setRegions(response.data);
                if (response.pagination) {
                    setPagination(response.pagination);
                }
                if (onDataChange) {
                    onDataChange();
                }
            }
        } catch (error) {
            console.error('Error fetching regions:', error);
            showError('Failed to fetch regions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
    };

    const handleAdd = () => {
        setEditingRegion(null);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleEdit = (region) => {
        setEditingRegion(region);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleView = (region) => {
        setEditingRegion(region);
        setViewMode(true);
        setIsModalVisible(true);
    };

    const handleDeleteClick = (region) => {
        setRegionToDelete(region);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (regionToDelete) {
            try {
                const response = await regionService.deleteRegion(regionToDelete.id);
                if (response.success) {
                    showSuccess('Region deleted successfully');
                    fetchRegions();
                } else {
                    showError(response.message || 'Failed to delete region');
                }
            } catch (error) {
                console.error('Error deleting region:', error);
                showError(error.message || 'Failed to delete region. Please try again.');
            }
        }
        setDeleteDialogOpen(false);
        setRegionToDelete(null);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingRegion(null);
        setViewMode(false);
    };

    const handleFormSuccess = () => {
        setIsModalVisible(false);
        setEditingRegion(null);
        setViewMode(false);
        fetchRegions();
        const action = editingRegion ? 'updated' : 'created';
        showSuccess(`Region ${action} successfully`);
    };

    // Handlers for popover
    const handlePopoverOpen = (event, type, data) => {
        if (type === 'cities') {
            setCitiesAnchorEl(event.currentTarget);
        } else {
            setZonesAnchorEl(event.currentTarget);
        }
        setSelectedMoreInfo(data);
    };

    const handlePopoverClose = (type) => {
        if (type === 'cities') {
            setCitiesAnchorEl(null);
        } else {
            setZonesAnchorEl(null);
        }
        setSelectedMoreInfo(null);
    };

    const renderConnectedCities = (region) => {
        const cities = region?.cities || [];
        
        if (cities.length === 0) {
            return <Chip label="No cities" size="small" color="default" />;
        }

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {cities.slice(0, 2).map((city, index) => (
                    <Chip 
                        key={index}
                        label={city.name}
                        size="small"
                        color="info"
                        variant="outlined"
                        icon={<LocationCityIcon />}
                    />
                ))}
                {cities.length > 2 && (
                    <>
                        <Chip 
                            label={`+${cities.length - 2} more`}
                            size="small"
                            variant="outlined"
                            onClick={(e) => handlePopoverOpen(e, 'cities', cities.slice(2))}
                            sx={{ cursor: 'pointer' }}
                        />
                        <Popover
                            open={Boolean(citiesAnchorEl)}
                            anchorEl={citiesAnchorEl}
                            onClose={() => handlePopoverClose('cities')}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            PaperProps={{
                                sx: {
                                    p: 2,
                                    maxWidth: 300,
                                    boxShadow: 2,
                                }
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Cities</Typography>
                            <Stack spacing={1}>
                                {selectedMoreInfo?.map((city, index) => (
                                    <Chip
                                        key={index}
                                        label={city.name}
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        icon={<LocationCityIcon />}
                                        sx={{ justifyContent: 'flex-start' }}
                                    />
                                ))}
                            </Stack>
                        </Popover>
                    </>
                )}
            </Box>
        );
    };

    const renderZoneCell = (region) => {
        const zones = region?.connected_zones || [];
        
        if (zones.length === 0) {
            return <Chip label="No Zone" size="small" color="default" />;
        }

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {zones.slice(0, 2).map((zone, index) => (
                    <Chip 
                        key={index}
                        label={zone.name}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        icon={<ZoneIcon />}
                    />
                ))}
                {zones.length > 2 && (
                    <>
                        <Chip 
                            label={`+${zones.length - 2} more`}
                            size="small"
                            variant="outlined"
                            onClick={(e) => handlePopoverOpen(e, 'zones', zones.slice(2))}
                            sx={{ cursor: 'pointer' }}
                        />
                        <Popover
                            open={Boolean(zonesAnchorEl)}
                            anchorEl={zonesAnchorEl}
                            onClose={() => handlePopoverClose('zones')}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            PaperProps={{
                                sx: {
                                    p: 2,
                                    maxWidth: 300,
                                    boxShadow: 2,
                                }
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Zones</Typography>
                            <Stack spacing={1}>
                                {selectedMoreInfo?.map((zone, index) => (
                                    <Chip
                                        key={index}
                                        label={zone.name}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        icon={<ZoneIcon />}
                                        sx={{ justifyContent: 'flex-start' }}
                                    />
                                ))}
                            </Stack>
                        </Popover>
                    </>
                )}
            </Box>
        );
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    // Server-side pagination - no client-side filtering needed

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <TextField
                    placeholder="Search regions..."
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    size="small"
                    sx={{ width: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Add New Region
                </Button>
            </Stack>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>S.No</TableCell>
                            <TableCell>Region Code</TableCell>
                            <TableCell>Region Name</TableCell>
                            <TableCell>Cities</TableCell>
                            <TableCell>Zone</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : regions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">
                                        No regions found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            regions.map((region, index) => (
                                <TableRow key={region.id}>
                                    <TableCell>{(pagination.current_page - 1) * pagination.per_page + index + 1}</TableCell>
                                    <TableCell>{region.code || '-'}</TableCell>
                                    <TableCell>{region.name}</TableCell>
                                    <TableCell>
                                        {renderConnectedCities(region)}
                                    </TableCell>
                                    <TableCell>
                                        {renderZoneCell(region)}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View Details">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleView(region)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Region">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleEdit(region)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Region">
                                                <IconButton 
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(region)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) => 
                        `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                />
            </TableContainer>

            {/* Edit/Add Modal */}
            <Modal
                open={isModalVisible}
                onClose={handleModalClose}
                aria-labelledby="region-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 600,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <Typography id="region-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        {viewMode ? 'View' : editingRegion ? 'Edit' : 'Add'} Region
                    </Typography>
                    <RegionForm
                        region={editingRegion}
                        onSuccess={handleFormSuccess}
                        onCancel={handleModalClose}
                        viewMode={viewMode}
                    />
                </Box>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Region
                </DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Are you sure you want to delete the region "{regionToDelete?.name}"?
                    </Typography>
                    {regionToDelete?.city_count > 0 && (
                        <Typography color="warning.main">
                            This region has {regionToDelete.city_count} associated {regionToDelete.city_count === 1 ? 'city' : 'cities'}. 
                            Deleting this region will automatically unassign {regionToDelete.city_count === 1 ? 'this city' : 'these cities'}.
                        </Typography>
                    )}
                    <Typography color="error" sx={{ mt: 1 }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RegionList; 